/**
 * Ghana-only product region — search, directions, and transport use Ghana data.
 */
import { isInGhana } from './geo';

export type RegionAvailability =
  | 'in_ghana'
  /** Browser GPS abroad but map view is over Ghana — we bias search to map center. */
  | 'outside_gps_map_in_ghana'
  /** Map and GPS both outside Ghana — location features are unavailable. */
  | 'outside';

export type ProximityCoords = { latitude: number; longitude: number };

export function regionAvailability(
  userLocation: { lat: number; lng: number } | null,
  mapCenter: { lat: number; lng: number } | null,
): RegionAvailability {
  if (userLocation && isInGhana(userLocation.lng, userLocation.lat)) {
    return 'in_ghana';
  }
  if (userLocation && !isInGhana(userLocation.lng, userLocation.lat)) {
    if (mapCenter && isInGhana(mapCenter.lng, mapCenter.lat)) {
      return 'outside_gps_map_in_ghana';
    }
    return 'outside';
  }
  // GPS unknown — treat map-in-Ghana as in-region (local users before fix, or location denied).
  if (mapCenter && isInGhana(mapCenter.lng, mapCenter.lat)) {
    return 'in_ghana';
  }
  return 'outside';
}

/** Prefer in-Ghana GPS; else map center in Ghana; ignore browser GPS outside Ghana. */
export function pickGhanaProximity(
  userLocation: { lat: number; lng: number } | null,
  mapCenter: { lat: number; lng: number } | null,
): ProximityCoords | undefined {
  if (userLocation && isInGhana(userLocation.lng, userLocation.lat)) {
    return { latitude: userLocation.lat, longitude: userLocation.lng };
  }
  if (mapCenter && isInGhana(mapCenter.lng, mapCenter.lat)) {
    return { latitude: mapCenter.lat, longitude: mapCenter.lng };
  }
  return undefined;
}

export function outOfRegionMessage(mode: RegionAvailability): string | null {
  switch (mode) {
    case 'in_ghana':
      return null;
    case 'outside_gps_map_in_ghana':
      return 'Coordal Maps is currently available in Ghana. Showing results near your map view in Ghana.';
    case 'outside':
      return 'Coordal Maps is currently available in Ghana. Pan to Ghana to explore the map, get directions, and use transport.';
  }
}

export function shouldWarnOnLocationFeature(
  userLocation: { lat: number; lng: number } | null,
  mapCenter: { lat: number; lng: number } | null,
): boolean {
  return regionAvailability(userLocation, mapCenter) !== 'in_ghana';
}

export type NavigationRegionBlockReason = 'origin' | 'destination' | 'both';

export interface NavigationRegionBlock {
  reason: NavigationRegionBlockReason;
  originName?: string;
  destinationName?: string;
}

/** Block live navigation when either endpoint lies outside Ghana service bounds. */
export function getNavigationRegionBlock(
  origin: { lat: number; lng: number; name?: string } | null | undefined,
  destination: { lat: number; lng: number; name?: string } | null | undefined,
): NavigationRegionBlock | null {
  const originIn = origin
    ? isInGhana(origin.lng, origin.lat)
    : true;
  const destinationIn = destination
    ? isInGhana(destination.lng, destination.lat)
    : true;

  if (originIn && destinationIn) return null;

  if (!originIn && !destinationIn) {
    return {
      reason: 'both',
      originName: origin?.name,
      destinationName: destination?.name,
    };
  }
  if (!originIn) {
    return { reason: 'origin', originName: origin?.name };
  }
  return { reason: 'destination', destinationName: destination?.name };
}

/** True when GPS is off/unavailable or abroad and no valid in-Ghana start is set yet. */
export function needsGhanaStartLocationPrompt(
  userLocation: { lat: number; lng: number } | null | undefined,
  origin: { coordinates: { lat: number; lng: number }; name: string } | null | undefined,
): boolean {
  if (
    origin
    && origin.name !== 'Your location'
    && isInGhana(origin.coordinates.lng, origin.coordinates.lat)
  ) {
    return false;
  }

  // Origin may already reflect simulated/device GPS while mapStore userLocation lags.
  if (origin?.name === 'Your location') {
    return !isInGhana(origin.coordinates.lng, origin.coordinates.lat);
  }

  if (userLocation && isInGhana(userLocation.lng, userLocation.lat)) {
    return false;
  }

  return true;
}

/** Both endpoints must exist and lie within Ghana before routing APIs run. */
export function canRequestDirectionsInGhana(
  origin: { coordinates: { lat: number; lng: number }; name?: string } | null | undefined,
  destination: { coordinates: { lat: number; lng: number }; name?: string } | null | undefined,
): boolean {
  if (!origin || !destination) return false;
  return getNavigationRegionBlock(
    { ...origin.coordinates, name: origin.name },
    { ...destination.coordinates, name: destination.name },
  ) === null;
}

/** Live turn-by-turn requires an enabled GPS fix inside Ghana. */
export function canStartLiveNavigation(
  userLocation: { lat: number; lng: number } | null | undefined,
): boolean {
  return Boolean(userLocation && isInGhana(userLocation.lng, userLocation.lat));
}

/** Live weather forecast and the daily map briefing require in-Ghana GPS. */
export function canShowWeatherForecast(
  userLocation: { lat: number; lng: number } | null | undefined,
): boolean {
  return Boolean(userLocation && isInGhana(userLocation.lng, userLocation.lat));
}

export function weatherForecastUnavailableMessage(
  userLocation: { lat: number; lng: number } | null | undefined,
): string {
  if (!userLocation) {
    return 'Weather forecast is unavailable in your location. Enable location services and try again when you are in Ghana.';
  }
  return 'Weather forecast is not available in your location. Coordal weather forecasts are currently available in Ghana only.';
}
